/**
 * "See changes": the saved draft against what is live, field by field
 * (third live review, 2026-09-27: "Draft v4 is saved but not live" gave no
 * clue what v4 changed). Leaves are compared as JSON, so a list (trades,
 * contact fields, result actions) is one row showing both lists.
 */
import { CONFIG_GROUPS, TRADES, type ConfigGroup, type WidgetConfigModel } from './model';

export const GROUP_LABELS: Record<ConfigGroup, string> = {
  branding: 'Branding',
  copy: 'Copy',
  placement: 'Placement',
  scope: 'Scope',
  safety: 'Safety',
  capture: 'Contact form',
  routing: 'After diagnosis',
  handoff: 'App handoff',
  analytics: 'Analytics',
};

/** Names for the fields people change most; the rest are humanized from their path. */
const FIELD_LABELS: Record<string, string> = {
  'branding.colors.primary': 'Brand colour',
  'branding.colors.on_primary': 'Button text colour',
  'branding.colors.surface': 'Card background',
  'branding.colors.text': 'Text colour',
  'branding.colors.border': 'Border colour',
  'branding.logo_url': 'Logo',
  'branding.theme': 'Theme',
  'branding.radius': 'Corner radius',
  'branding.font_family': 'Font',
  'branding.powered_by': '“Powered by FinderlyFix”',
  'copy.headline': 'Headline',
  'copy.subheadline': 'Subtitle',
  'copy.cta_route': 'Send button label',
  'copy.cta_app': 'App button label',
  'copy.disclaimer_md': 'Disclaimer',
  'copy.notice_at_collection_md': 'Notice at collection',
  'copy.out_of_scope_message': 'Out-of-scope message',
  'copy.live_notice': 'Live conversation notice (full)',
  'copy.live_notice_short': 'Live conversation notice (short line)',
  'copy.chat_notice': 'Chat notice',
  'scope.trades': 'Trades',
  'scope.modes.photo': 'Photo',
  'scope.modes.video': 'Video',
  'scope.modes.audio': 'Sound',
  'scope.modes.live': 'Live conversation',
  'scope.modes.chat': 'Chat',
  'scope.default_mode': 'Tab visitors land on',
  'scope.out_of_scope_behavior': 'Problems outside your trades',
  'scope.service_area.mode': 'Service area',
  'scope.service_area.postal_codes': 'ZIP codes',
  'scope.service_area.out_of_area_behavior': 'Outside your area',
  'safety.emergency_bypass': 'Emergency bypass',
  'safety.emergency_phone': 'Emergency line',
  'safety.emergency_message_md': 'Emergency message',
  'safety.low_confidence_threshold': 'Low-confidence threshold',
  'safety.low_confidence_behavior': 'When the diagnosis is unsure',
  'capture.fields.required': 'Required fields',
  'capture.fields.optional': 'Optional fields',
  'capture.prefill_from_identity_token': 'Filled from your login',
  'capture.consent.mode': 'Consent model',
  'capture.consent.text_md': 'Consent text',
  'capture.consent.sms_opt_in': 'SMS opt-in',
  'routing.mode': 'Onward action',
  'routing.actions': 'Result buttons',
  'routing.diy_first': 'DIY steps first',
  'routing.primary_cta': 'Prominent button',
  'handoff.app_enabled': 'App handoff',
  'placement.seed_context_allowed': 'Accept details from your page',
  'placement.identity_token_required': 'Only for signed-in visitors',
  'placement.size': 'Card size',
};

export interface ConfigChange {
  path: string;
  group: ConfigGroup;
  label: string;
  before: unknown;
  after: unknown;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return Boolean(v) && typeof v === 'object' && !Array.isArray(v);
}

function humanizeKey(key: string): string {
  const s = key.replace(/_md$/, '').replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function fieldLabel(path: string): string {
  if (FIELD_LABELS[path]) return FIELD_LABELS[path];
  const parent = Object.keys(FIELD_LABELS).find((k) => path.startsWith(`${k}.`));
  if (parent) return `${FIELD_LABELS[parent]} › ${humanizeKey(path.slice(parent.length + 1).split('.').join(' '))}`;
  return path.split('.').slice(1).map(humanizeKey).join(' › ') || humanizeKey(path);
}

function walk(group: ConfigGroup, path: string, before: unknown, after: unknown, out: ConfigChange[]): void {
  if (isPlainObject(before) && isPlainObject(after) && !FIELD_LABELS[path]) {
    for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) walk(group, `${path}.${key}`, before[key], after[key], out);
    return;
  }
  if (JSON.stringify(before ?? null) === JSON.stringify(after ?? null)) return;
  out.push({ path, group, label: fieldLabel(path), before, after });
}

/** Every field that differs between `live` and `draft`, in the editor's group order. */
export function configDiff(live: WidgetConfigModel, draft: WidgetConfigModel): ConfigChange[] {
  const out: ConfigChange[] = [];
  for (const group of CONFIG_GROUPS) walk(group, group, live[group], draft[group], out);
  return out;
}

/** A value as the diff shows it. */
export function formatConfigValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return 'Not set (default)';
  if (typeof v === 'boolean') return v ? 'On' : 'Off';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) {
    if (!v.length) return 'None';
    return v.map((x) => (isPlainObject(x) ? String(x.label ?? x.id ?? JSON.stringify(x)) : String(x))).join(', ');
  }
  if (isPlainObject(v)) {
    return Object.entries(v)
      .filter(([, x]) => x !== null && x !== undefined)
      .map(([k, x]) => `${humanizeKey(k)}: ${formatConfigValue(x)}`)
      .join(' · ');
  }
  return JSON.stringify(v);
}

/** A changed value as the diff shows it, with trade slugs as their names. */
export function formatChangeValue(path: string, v: unknown): string {
  if (path === 'scope.trades' && Array.isArray(v)) {
    return v.length ? v.map((t) => TRADES.find((x) => x.slug === t)?.label ?? String(t)).join(', ') : 'None';
  }
  return formatConfigValue(v);
}
